import { FC, useCallback, useEffect, useState } from 'react';
import { lddGlobalSessionInfo } from '../../../Components/navigationTypes';
import { useAppSelector } from '../../../redux/hooks';
import { Card, Alert, Form, Col, Row, InputGroup, Badge, Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'react-bootstrap';
import TermsModal from './TermsModal';
import { setShowRenewSession } from '../../../redux/reducer';
import { useDispatch } from "react-redux";


type SubscriptionStatus = 'Purchased' | 'Expired' | 'None' | 'Default'; // Default when no lawyer selected
type PurchaseResponse = {
  ok: boolean,
  expiryDate?: string,
  error?: string
}

const LawyerContainer: FC<{
  LSUCID: string,
  setLSUCID: (selectLSUCID: string) => void,
  setSubscribed: (subscribed: boolean) => void,
  setLoaded: (loaded: boolean) => void
}> = ({ LSUCID, setLSUCID, setSubscribed, setLoaded }) => {
  const dispatch = useDispatch();
  const SESSION_INFO: lddGlobalSessionInfo = useAppSelector((g) => g.SessionInfo);
  const [Lawyer, setLawyer] = useState<string>("");
  const [expiryDateStr, setExpiryDateStr] = useState<string>("");
  const [subscibeStatus, setSubscribeStatus] = useState<SubscriptionStatus>("None");
  const [isPaymentInfoInvalid, setIsPaymentInfoInvalid] = useState(false);
  const [shouldShowTerms, setShouldShowTerms] = useState(false);
  const showTermsOfUse = () => setShouldShowTerms(true);
  const closeTermsOfUse = () => setShouldShowTerms(false);
  const [shouldShowSubscriptionAlert, setShouldShowSubscriptionAlert] = useState(false);
  const showSubscriptionAlert = () => setShouldShowSubscriptionAlert(true);
  const closeSubscriptionAlert = () => setShouldShowSubscriptionAlert(false);
  const [purchaseRes, setPurchaseRes] = useState<PurchaseResponse>({ok: false});
  
  const fetchData = useCallback(async (firmId: number, LSUCID: string, sid: string) => {
    if (firmId && LSUCID && LSUCID > "" && sid) {
      var responseData;
      try {
        setLoaded(false);
        responseData = await fetch(`${process.env.REACT_APP_DSAPI_ENDPOINT}/docservice/${firmId}/${LSUCID}/purchaseStatus?SessionID=${sid}`, {
          method: "GET",
          credentials: "include", // send user credentials for same origin and cors
          headers: {
              "Content-Type": "application/json"
          },
        });
        if (responseData.ok) {
          const data = await responseData.json();
          if (data.ok) {
            switch (data.result.purchaseStatus) {
              case 'Purchased':
                setSubscribeStatus('Purchased');
                setSubscribed(true);
                setExpiryDateStr(formatExpiryDateStr(data.result.expiryDate));
                break;
              case 'None':
                await fetchPaymentCheckData(firmId, LSUCID, sid);
                setExpiryDateStr("");
                setSubscribed(false);
                setSubscribeStatus("None");
                break;
              case 'Expired':
                await fetchPaymentCheckData(firmId, LSUCID, sid);
                setExpiryDateStr("");
                setSubscribed(false);
                setSubscribeStatus("Expired");
                break;
            }
          } else {
            throw data.error;
          }
        } else if (responseData.status == '403') {
          // timed out
          dispatch(setShowRenewSession(true));
        }
      } catch (e) {
        console.log(e);
        Alert(typeof e === 'string' ? e : e.message);
      } finally {
        setLoaded(true);
      }
    }
  }, [LSUCID]);

  useEffect(() => {
    if (!LSUCID || LSUCID <= "") {
      setSubscribeStatus('Default');
      setSubscribed(false);
    }
    fetchData(SESSION_INFO.firm.id, LSUCID, SESSION_INFO.sid);
    
  }, [LSUCID]);

  useEffect(() => {
    // on load
    if (SESSION_INFO.isLDDUser === false) {
      setLawyer(String(SESSION_INFO.firm.id));
      setLSUCID(String(SESSION_INFO.firm.id));
    }
  }, [])

  async function fetchPaymentCheckData(firmId: number, LSUCID: string, sid: string) {
    try {
      const responseData = await fetch(`${process.env.REACT_APP_DSAPI_ENDPOINT}/docservice/${firmId}/${LSUCID}/paymentHistory?SessionID=${sid}`, {
        method: "GET",
        credentials: "include", // send user credentials for same origin and cors
        headers: {
            "Content-Type": "application/json"
        },
      });
      if (responseData.ok) {
        const data = await responseData.json();
          setIsPaymentInfoInvalid(!data.ok);
      }
    } catch (error) {
      console.log(error.message);
    }
  }

  function formatExpiryDateStr(date: string): string {
    const dateStr = new Date(date).toDateString();
    var res = dateStr.split(" ");
    res.shift();
    return res.join("/");
  }
  
  async function makePurchase() {
    if (!SESSION_INFO.firm.id || !LSUCID || LSUCID <= "") return;
    try {
      setLoaded(false);
      const responseData = await fetch(`${process.env.REACT_APP_DSAPI_ENDPOINT}/docservice/purchase/1/${SESSION_INFO.firm.id}/${LSUCID}?SessionID=${SESSION_INFO.sid}`, {
        method: "POST",
        credentials: "include", // send user credentials for same origin and cors
        headers: {
            "Content-Type": "application/json"
        },
      });
      if (responseData.ok) {
        const data = await responseData.json();
        if (data && data.ok) {
          setSubscribeStatus('Purchased');
          setExpiryDateStr(formatExpiryDateStr(data.result.expiryDate));
          setSubscribed(true);
          setPurchaseRes({ok: true, expiryDate: data.result?.expiryDate});
        } else if (data && !data.ok) {
          setPurchaseRes({ok: false, error: data.error});
        }
      }
    } catch (error) {
      console.log(error);
      setPurchaseRes({ok: false, error: error.message});
    } finally {
      setLoaded(true);
      closeTermsOfUse();
      showSubscriptionAlert();
    }
  }

  
  const SubscriptionAlert: React.FC = () => {
    return (
        <Modal show={shouldShowSubscriptionAlert} onHide={closeSubscriptionAlert}>
            <ModalHeader>
                OREA Subscription {purchaseRes.ok ? `Successful` : `Failed`}
            </ModalHeader>
            <ModalBody>
              {purchaseRes.ok ? `You have successfully subscribed to OREA. This subscription will expire on ${formatExpiryDateStr(purchaseRes.expiryDate)}.` : purchaseRes.error}
            </ModalBody>
            <ModalFooter>
                <Button variant="primary" onClick={closeSubscriptionAlert}>OK</Button>
            </ModalFooter>
        </Modal>
    );
  }

  return (
    <>
      <Card>
        <Card.Header>
          <Card.Title>OREA Forms Subscription</Card.Title>
          {(subscibeStatus === 'Purchased') && 
            <>
              <Badge bg="info" text="dark" pill style={{position: 'absolute', right: '30px'}}>
                Active {/* to {expiryDateStr} */}
              </Badge>
            </>
          }
          {(subscibeStatus === 'Expired' || subscibeStatus === 'None') && 
            <>
              <Badge bg="danger" text="dark" pill style={{position: 'absolute', right: '30px'}}>
                Inactive
              </Badge>
            </>
          }
        </Card.Header>

        <Card.Body>
            {(!Lawyer || Lawyer <= "") &&
                <Alert className="mb-4">Please select a lawyer.</Alert>
            }
            
            {(subscibeStatus === 'Expired' || subscibeStatus === 'None') &&
                (isPaymentInfoInvalid ?
                <Alert className="mb-4" variant='danger'>
                Your account has not been active for some time. Please phone us on 800-363-2253 ext. 0 or complete and return <a href="https://static1.squarespace.com/static/58bd70f0be6594678d011645/t/5e5ea6f43d5ace6180d73bd0/1583261430067/LDD-PaymentUpdate-Form-2020.pdf"><b>this form</b></a> by fax or email to verify/update your payment information and reactivate your account.
                </Alert>
                :
                <Alert className="mb-4" variant='danger'>
                No active subscription associated with this lawyer. Please click 'Subscribe' to access the latest OREA forms.
                </Alert>)
            }

            <Row className="mb-4">
              <Col className="col-3">
                <Form.Label>Lawyer</Form.Label>
                <Form.Select
                  onChange={(e) => {
                    setLawyer(e.target.value);
                    setLSUCID(e.target.value ? e.target.selectedOptions[0].innerText : '');
                  }}
                  value={Lawyer }>
                    {SESSION_INFO.isLDDUser 
                      ? 
                      <>
                       <option value="">select lawyer..</option>
                       {SESSION_INFO.firm.Lawyers.map(lawyer =>
                        <option key={lawyer.LSUCID} value={`${lawyer.FirstName} ${lawyer.LastName}`} label={`${lawyer.FirstName} ${lawyer.LastName}`}>{lawyer.LSUCID}</option>)
                       }
                      </>
                      : <option key={SESSION_INFO.firm.id} value={`${SESSION_INFO.user.firstname} ${SESSION_INFO.user.lastname}`} label={`${SESSION_INFO.user.firstname} ${SESSION_INFO.user.lastname}`}>{SESSION_INFO.user.fullname}</option>                    }
                  
                </Form.Select>
              </Col>

              {(subscibeStatus === 'Purchased') && 
                <Col className='col-3'>
                  <Form.Label>Expiry Date</Form.Label>
                  <Form.Control value={expiryDateStr} readOnly></Form.Control>
                </Col>
              }

              {(subscibeStatus === 'Expired' || subscibeStatus === 'None') && 
                <>
                    <Col className='col-2'>
                        <Form.Label>Subscription Fee</Form.Label>
                        <Form.Control value="$120.00" readOnly />
                    </Col>
                    <Col className='col-3'>
                        <Button variant='primary' disabled={isPaymentInfoInvalid} id='btn-subscribe' style={{marginTop:"15px"}} onClick={showTermsOfUse}>Subscribe</Button>
                    </Col>
                </>
              }
              
            </Row>

            {(subscibeStatus === 'Expired' || subscibeStatus === 'None' || subscibeStatus === 'Default') &&
                <>
                    <p><small>The annual license fee and terms of use regarding the OREA Standard Forms are set by OREA not LDD. Limited personal licenses are granted to individual lawyers within a law firm upon payment of the annual license fee by each lawyer within a firm who chooses to use the forms. Per OREA's requirements, the licensed lawyer's name is printed at the bottom of each form.</small></p>
                </>
            }
        </Card.Body>
      </Card>

      <TermsModal show={shouldShowTerms} onHide={closeTermsOfUse} purchase={makePurchase}/>

      <SubscriptionAlert />
    </>
  );
};

export default LawyerContainer;